import { CHAPTER_CONTAINER_ID } from 'components/Player/components/Chapters/Chapters'
import { HAS_CHANGED_CAPTION } from './plugins/CaptionEventHandler/CaptionEventHandler'
import translations from './translations'
import { CleanUpTask, Plugin, UrPlayerConfig } from './types'
import {
  AddButton,
  addDisplayControls,
  addOptionControls,
} from './utils/addOptionControls'
import getPlayerWithInterceptedEvent from './utils/jwPlayerEventInterceptor'
import {
  buildTracks,
  PLAYER_CHAPTER_BUTTON_ID,
  toggleChapterButtonAttributes,
} from './utils/urPlayerUtils'

export interface PlayerSetupOptions {
  autostart: 'viewable' | false
  starttime: number
  preload: 'none' | 'metadata' | 'auto'
}

class UrPlayer {
  private readonly SECONDS_FORWARD_BUTTON = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"></svg>`

  private readonly BLANK_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"></svg>`

  private cleanUpTasks: CleanUpTask[] = []

  private restarting = false

  private plugins: Plugin[] | null = null

  private readonly defaultOptions: PlayerSetupOptions = {
    autostart: false,
    starttime: 0,
    preload: 'none',
  }

  constructor(
    private readonly elementId: string,
    private readonly config: UrPlayerConfig,
  ) {}

  setup = (options: Partial<PlayerSetupOptions>): Promise<void> => {
    const { autostart, starttime, preload } = {
      ...this.defaultOptions,
      ...options,
    }

    return new Promise((resolve, reject) => {
      this.player.on('setupError', (err: { message: string }) => {
        reject(
          new Error(
            `Unable to setup UrPlayer due to setup error in JW player: ${err.message}`,
          ),
        )
      })

      const formatConfig = { aspectratio: '16:9' }

      const chromeCastConfig = this.config.chromecastReceiverId
        ? {
            cast: { appid: this.config.chromecastReceiverId },
          }
        : {}

      this.player.setup({
        ...formatConfig,
        width: '100%',
        ...translations,
        displaytitle: false,
        pipIcon: 'disabled',
        playlist: [
          {
            title: this.config.programTitle,
            sources: [
              {
                file: this.config?.mediaStream?.sources.hls ?? '',
              },
            ],
            starttime,
            tracks: buildTracks(
              this.config.urAssetId,
              this.config.format,
              this.config.chapters,
              this.config.includeChapters,
            ),
            image: this.config.imageUrl,
          },
        ],
        skin: {
          name: 'ur-player',
        },
        autostart,
        preload,
        ...chromeCastConfig,
        playbackRateControls: true,
        playbackRates: [
          0.25, 0.5, 0.75, 0.8, 0.85, 0.9, 0.95, 1, 1.05, 1.1, 1.15, 1.2, 1.25,
          1.5, 1.75, 2,
        ],
      })

      const NETWORK_ERROR_CODE = 230002

      this.player.on('error', (error: { code: number }) => {
        if (error.code === NETWORK_ERROR_CODE) this.checkConnectionAndSetup()
      })

      this.player.on('captionsList', e => {
        if (!localStorage.getItem(HAS_CHANGED_CAPTION) && e.tracks.length > 1) {
          const allCaptions: { label: string }[] = this.player.getCaptionsList()
          const defaultCaption = allCaptions.find(
            caption => caption.label === 'Svenska',
          )

          if (defaultCaption) {
            this.player.setCurrentCaptions(allCaptions.indexOf(defaultCaption))
          } else {
            const FIRST_AVAILABLE_CAPTION = 1
            this.player.setCurrentCaptions(FIRST_AVAILABLE_CAPTION)
          }
        }
      })
      this.player.on('ready', () => {
        this.removeNextEpisodeButton()

        this.addVideoScrubForwardButton()
        if (this.config.chapters && this.config.includeChapters) {
          this.addChaptersButton()
        }

        resolve()
      })
    })
  }

  remove = (): Promise<void> => {
    return new Promise(resolve => {
      this.cleanUpTasks.forEach(task => task())
      this.cleanUpTasks = []

      this.player.on('remove', () => {
        resolve()
      })

      this.player.remove()
    })
  }

  private async checkConnectionAndSetup() {
    const checkConnection = setInterval(async () => {
      if (navigator.onLine) {
        clearInterval(checkConnection)
      }
    }, 1000)
  }

  registerPlugins = (plugins: Plugin[]) => {
    const jwPlayer = getPlayerWithInterceptedEvent(
      this.player,
      ['play', 'firstFrame'],
      (originalHandler, ...args) => {
        if (!this.restarting) originalHandler(...args)
      },
    )

    const hasNotInitialized = this.plugins === null
    if (hasNotInitialized) {
      this.plugins = plugins
    }

    this.plugins?.forEach(plugin => {
      plugin(jwPlayer, this.config)
    })
  }

  private get player() {
    // @ts-expect-error the jwplayer types does not know that jwplayer is callable
    return window.jwplayer(this.elementId)
  }

  private readonly removeNextEpisodeButton = () => {
    document.getElementsByClassName('jw-display-icon-next')[0].remove()
  }

  private readonly addVideoScrubForwardButton = () => {
    const SECONDS_10 = 10

    const cleanUpTasks = addOptionControls(
      [],
      this.getCurriedAddButton(this.SECONDS_FORWARD_BUTTON),
      'scrub-forward-button',
      'Spola fram 10 sekunder',
      () => {
        this.player.seek(this.player.getPosition() + SECONDS_10)
      },
      2,
    )

    this.cleanUpTasks.push(...cleanUpTasks)

    this.addScrubForwardButtonMobile()
  }

  private readonly addScrubForwardButtonMobile = () => {
    const SECONDS_10 = 10

    const cleanUpTasks = addDisplayControls(
      this.getCurriedAddButton(this.SECONDS_FORWARD_BUTTON),
      'mobile-scrub-forward-button',
      () => {
        this.player.seek(this.player.getPosition() + SECONDS_10)
      },
    )

    this.cleanUpTasks.push(...cleanUpTasks)
  }

  private readonly addChaptersButton = () => {
    this.player.addButton(
      this.BLANK_SVG,
      'Visa kapitel',
      () => {
        if (this.config.chapterCallBack) {
          this.config.chapterCallBack(this.player.getChapters())
        }
        toggleChapterButtonAttributes()
      },
      PLAYER_CHAPTER_BUTTON_ID,
      'chapters-button-container',
    )

    const chapterButtonContainer = document.querySelector(
      `div[button="${PLAYER_CHAPTER_BUTTON_ID}"]`,
    )

    chapterButtonContainer?.setAttribute('aria-expanded', 'false')
    chapterButtonContainer?.setAttribute('aria-controls', CHAPTER_CONTAINER_ID)
  }

  private readonly getCurriedAddButton = (svg: string): AddButton => {
    return (onClick, id, className) =>
      this.player.addButton(svg, '', onClick, id, className)
  }
}

export default UrPlayer
